/** 经营总览：物流阶段与支付节点占比 */

import { sumOrderOpenedArByTitle } from '../../../../../utils/order/orderAccountsReceivable';
import { formatDonutCenter, formatOverviewStageMetric } from './overviewMetricsFormat';

/** 第三行物流卡：待发货 → 已发货 → 物流完成 → 已关单（互斥；已关单优先） */
export const OVERVIEW_LOGISTICS_SEGMENTS = [
	{ key: 'waitingDelivery', label: '待发货', milestone: null },
	{ key: 'shipped', label: '已发货', milestone: '发货' },
	{ key: 'logisticsComplete', label: '物流完成', milestone: '验收' },
	{ key: 'closed', label: '已关单', milestone: '完成', tone: 'success' },
];

/** 第三行财务卡色块：已开应收 title×已付/未付 */
export const OVERVIEW_PAYMENT_NODE_SHARE_ROWS = [
	{ key: 'prepay', label: '预付', arTitle: '预付' },
	{ key: 'delivery', label: '发货尾款', arTitle: '发货/提供服务' },
	{ key: 'accept', label: '验收尾款', arTitle: '验收' },
];

const OVERVIEW_EXCLUDED_PHASES = new Set(['pendingApproval', 'approvalRejected']);

const isOverviewPipelineOrder = (order) => {
	const phase = order?.primaryPhases?.operational;
	if (OVERVIEW_EXCLUDED_PHASES.has(phase)) return false;
	return true;
};

/**
 * 与后端 orderOverviewPipeline / isEffectivelyCompletedOrder 关单口径一致：
 * 待核验优先（uiHints 或 primaryPhases.operational）；统计列表行无 uiHints，必须看 phase。
 */
export const isOverviewClosedOrder = (order) => {
	if (order?.uiHints?.isPendingFinalCostConfirmation || order?.primaryPhases?.operational === 'pendingReview') {
		return false;
	}
	if (order?.isCompleted || order?.isFinalCostConfirmed) return true;
	return order?.primaryPhases?.operational === 'completed';
};

const resolveOverviewLogisticsKey = (order) => {
	if (isOverviewClosedOrder(order)) return 'closed';
	const delivery = order?.deliveryStatus;
	if (delivery === 'waitingDelivery') return 'waitingDelivery';
	if (delivery === 'waitingAccept') return 'shipped';
	if (delivery === 'logisticsComplete') return 'logisticsComplete';
	return null;
};

const resolveOverviewFinanceKey = (order) => {
	if (isOverviewClosedOrder(order)) return 'closed';
	const phase = order?.primaryPhases?.operational;
	if (phase === 'pendingReview') return 'pendingReview';
	const payment = order?.paymentStatus;
	if (payment === 'payComplete') return 'payComplete';
	if (payment === 'waitingPayAccept') return 'waitingPayAccept';
	if (payment === 'waitingPayDelivery') return 'waitingPayDelivery';
	if (payment === 'waitingPayCredit') return 'waitingPayCredit';
	if (payment === 'waitingPayPlaceOrder' || phase === 'waitingPrepay') return 'waitingPrepay';
	return null;
};

const createEmptyStageBucket = () => ({ count: 0, totalPrice: 0 });

const formatSharePercent = (part, total) => {
	if (!(total > 0) || !(part > 0)) return '0%';
	const ratio = (part / total) * 100;
	if (ratio >= 99.95) return '100%';
	if (ratio >= 10) return `${ratio.toFixed(0)}%`;
	return `${ratio.toFixed(2)}%`;
};

/** 已收合计等顶栏占比：始终保留两位小数 */

/** 已收合计等顶栏占比：始终保留两位小数 */
const formatSharePercentFixed2 = (part, total) => {
	if (!(total > 0) || !(part > 0)) return '0.00%';
	const ratio = (part / total) * 100;
	if (ratio >= 99.995) return '100.00%';
	return `${ratio.toFixed(2)}%`;
};

/** 分段顶栏：¥47.8k · 35单 */

/** 物流横向进度条数据 */
export const buildOverviewLogisticsBundle = (orders = []) => {
	const buckets = Object.fromEntries(OVERVIEW_LOGISTICS_SEGMENTS.map((item) => [item.key, createEmptyStageBucket()]));
	for (const order of orders ?? []) {
		if (!isOverviewPipelineOrder(order)) continue;
		const key = resolveOverviewLogisticsKey(order);
		if (!key || !buckets[key]) continue;
		buckets[key].count += 1;
		buckets[key].totalPrice += Number(order.totalPrice) || 0;
	}

	return finalizeOverviewLogisticsFromBuckets(buckets);
};

const finalizeOverviewLogisticsFromBuckets = (buckets) => {
	const totalPrice = OVERVIEW_LOGISTICS_SEGMENTS.reduce((sum, item) => sum + buckets[item.key].totalPrice, 0);
	const totalCount = OVERVIEW_LOGISTICS_SEGMENTS.reduce((sum, item) => sum + buckets[item.key].count, 0);
	const weightTotal = totalPrice > 0 ? totalPrice : totalCount;

	/** 非空段设下限后归一化，避免保底把待发货等大段压「看起来不变」；0 单 flex=0 塌缩 */
	const MIN_NONEMPTY_SHARE = 0.06;
	const rawShares = OVERVIEW_LOGISTICS_SEGMENTS.map((meta) => {
		const bucket = buckets[meta.key];
		const weight = totalPrice > 0 ? bucket.totalPrice : bucket.count;
		if (!(weightTotal > 0) || !(bucket.count > 0)) return 0;
		return Math.max(weight / weightTotal, MIN_NONEMPTY_SHARE);
	});
	const rawShareSum = rawShares.reduce((sum, share) => sum + share, 0);
	const equalShare = 1 / OVERVIEW_LOGISTICS_SEGMENTS.length;

	const segments = OVERVIEW_LOGISTICS_SEGMENTS.map((meta, index) => {
		const bucket = buckets[meta.key];
		const weight = totalPrice > 0 ? bucket.totalPrice : bucket.count;
		const flex = weightTotal > 0 ? (rawShareSum > 0 ? rawShares[index] / rawShareSum : 0) : equalShare;
		return {
			key: meta.key,
			label: meta.label,
			milestone: meta.milestone,
			tone: meta.tone || 'primary',
			count: bucket.count,
			totalPrice: bucket.totalPrice,
			metricLabel: formatOverviewStageMetric(bucket.totalPrice, bucket.count),
			shareLabel: formatSharePercent(weight, weightTotal),
			flex,
		};
	});

	return { totalPrice, totalCount, segments };
};

/** 优先 overviewPipeline.logistics */

/** 优先 overviewPipeline.logistics */
export const buildOverviewLogisticsBundleFromStats = (stats) => {
	const pipeline = stats?.overviewPipeline?.logistics;
	if (pipeline && typeof pipeline === 'object') {
		const buckets = Object.fromEntries(
			OVERVIEW_LOGISTICS_SEGMENTS.map((item) => [
				item.key,
				{
					count: Number(pipeline[item.key]?.count) || 0,
					totalPrice: Number(pipeline[item.key]?.totalPrice) || 0,
				},
			]),
		);
		return finalizeOverviewLogisticsFromBuckets(buckets);
	}
	return buildOverviewLogisticsBundle(stats?.orders);
};

const createEmptyPaymentNodeBucket = () => ({ paid: 0, pending: 0, paidCount: 0, pendingCount: 0 });

/** 2 列 × 3 行：左已收、右待收；行=预付 / 发货 / 验收 */

/** 2 列 × 3 行：左已收、右待收；行=预付 / 发货 / 验收 */
const PAYMENT_NODE_WALL_SEGMENTS = [
	{ key: 'prepay-paid', rowKey: 'prepay', field: 'paid', label: '预付', tone: 'prepay-paid', gridRow: 1, gridCol: 1 },
	{ key: 'prepay-pending', rowKey: 'prepay', field: 'pending', label: '待预付', tone: 'prepay-pending', gridRow: 1, gridCol: 2 },
	{ key: 'delivery-paid', rowKey: 'delivery', field: 'paid', label: '发货', tone: 'delivery-paid', gridRow: 2, gridCol: 1 },
	{ key: 'delivery-pending', rowKey: 'delivery', field: 'pending', label: '发货尾款', tone: 'delivery-pending', gridRow: 2, gridCol: 2 },
	{ key: 'accept-paid', rowKey: 'accept', field: 'paid', label: '验收', tone: 'accept-paid', gridRow: 3, gridCol: 1 },
	{ key: 'accept-pending', rowKey: 'accept', field: 'pending', label: '验收尾款', tone: 'accept-pending', gridRow: 3, gridCol: 2 },
];

const PAYMENT_NODE_CELL_LABELS = {
	prepay: { paid: '预付', pending: '待预付' },
	delivery: { paid: '发货', pending: '发货尾款' },
	accept: { paid: '验收', pending: '验收尾款' },
};

export const OVERVIEW_PAYMENT_NODE_METRIC_OPTIONS = [
	{ id: 'amount', label: '金额' },
	{ id: 'count', label: '单量' },
];

const buildPaymentNodeCell = ({ key, label, tone, amount, orderCount, amountTotal, countTotal, amountRowTotal, countRowTotal }) => {
	const amountFlex = amountRowTotal > 0 ? amount / amountRowTotal : 0.5;
	const countFlex = countRowTotal > 0 ? orderCount / countRowTotal : 0.5;
	return {
		key,
		label,
		tone,
		amount,
		orderCount,
		amountLabel: formatDonutCenter(amount),
		countLabel: `${Number(orderCount) || 0}单`,
		amountShareLabel: formatSharePercent(amount, amountTotal),
		countShareLabel: formatSharePercent(orderCount, countTotal),
		amountFlex,
		countFlex,
		/** 默认金额视图字段，卡片切换时再投影 */
		value: amount,
		valueLabel: formatDonutCenter(amount),
		shareLabel: formatSharePercent(amount, amountTotal),
		flex: amountFlex,
	};
};

const finalizeOverviewPaymentNodeShareBundle = (buckets, pendingReviewBucket = null, closedBucket = null) => {
	const nodeRows = OVERVIEW_PAYMENT_NODE_SHARE_ROWS.map((meta) => {
		const paid = Number(buckets?.[meta.key]?.paid) || 0;
		const pending = Number(buckets?.[meta.key]?.pending) || 0;
		const paidCount = Number(buckets?.[meta.key]?.paidCount) || 0;
		const pendingCount = Number(buckets?.[meta.key]?.pendingCount) || 0;
		const total = paid + pending;
		const orderCount = paidCount + pendingCount;
		return {
			key: meta.key,
			label: meta.label,
			paid,
			pending,
			paidCount,
			pendingCount,
			total,
			orderCount,
			paidLabel: formatDonutCenter(paid),
			pendingLabel: formatDonutCenter(pending),
		};
	});

	const totalAmount = nodeRows.reduce((sum, row) => sum + row.total, 0);
	const totalOrderCount = nodeRows.reduce((sum, row) => sum + row.orderCount, 0);
	const paidTotal = nodeRows.reduce((sum, row) => sum + row.paid, 0);
	const paidOrderCount = nodeRows.reduce((sum, row) => sum + row.paidCount, 0);
	const equalRow = 1 / Math.max(1, OVERVIEW_PAYMENT_NODE_SHARE_ROWS.length);

	const rows = nodeRows.map((row) => {
		const cellLabels = PAYMENT_NODE_CELL_LABELS[row.key] || { paid: row.label, pending: row.label };
		return {
			key: row.key,
			label: row.label,
			total: row.total,
			orderCount: row.orderCount,
			amountFlex: totalAmount > 0 ? row.total / totalAmount : equalRow,
			countFlex: totalOrderCount > 0 ? row.orderCount / totalOrderCount : equalRow,
			flex: totalAmount > 0 ? row.total / totalAmount : equalRow,
			paid: buildPaymentNodeCell({
				key: `${row.key}-paid`,
				label: cellLabels.paid,
				tone: `${row.key}-paid`,
				amount: row.paid,
				orderCount: row.paidCount,
				amountTotal: totalAmount,
				countTotal: totalOrderCount,
				amountRowTotal: row.total,
				countRowTotal: row.orderCount,
			}),
			pending: buildPaymentNodeCell({
				key: `${row.key}-pending`,
				label: cellLabels.pending,
				tone: `${row.key}-pending`,
				amount: row.pending,
				orderCount: row.pendingCount,
				amountTotal: totalAmount,
				countTotal: totalOrderCount,
				amountRowTotal: row.total,
				countRowTotal: row.orderCount,
			}),
		};
	});

	const segments = PAYMENT_NODE_WALL_SEGMENTS.map((meta) => {
		const row = nodeRows.find((item) => item.key === meta.rowKey);
		const amount = Number(row?.[meta.field]) || 0;
		const orderCount = Number(row?.[`${meta.field}Count`]) || 0;
		return {
			key: meta.key,
			label: meta.label,
			tone: meta.tone,
			amount,
			orderCount,
			gridRow: meta.gridRow,
			gridCol: meta.gridCol,
		};
	});

	return {
		totalAmount,
		totalOrderCount,
		totalLabel: formatDonutCenter(totalAmount),
		totalCountLabel: `${totalOrderCount}单`,
		paidTotal,
		paidOrderCount,
		paidShareLabel: totalAmount > 0 ? formatSharePercentFixed2(paidTotal, totalAmount) : '0.00%',
		paidCountShareLabel: totalOrderCount > 0 ? formatSharePercentFixed2(paidOrderCount, totalOrderCount) : '0.00%',
		rows,
		columns: rows,
		segments,
		nodeRows,
		pendingReview: {
			count: Number(pendingReviewBucket?.count) || 0,
			totalPrice: Number(pendingReviewBucket?.totalPrice) || 0,
			metricLabel: formatOverviewStageMetric(Number(pendingReviewBucket?.totalPrice) || 0, Number(pendingReviewBucket?.count) || 0),
		},
		closed: {
			count: Number(closedBucket?.count) || 0,
			totalPrice: Number(closedBucket?.totalPrice) || 0,
			metricLabel: formatOverviewStageMetric(Number(closedBucket?.totalPrice) || 0, Number(closedBucket?.count) || 0),
		},
	};
};

/** 按金额 / 订单量投影六色墙展示字段 */

/** 按金额 / 订单量投影六色墙展示字段 */
export const projectOverviewPaymentNodeShareBundle = (bundle, metric = 'amount') => {
	const useCount = metric === 'count';
	const sourceRows = Array.isArray(bundle?.rows) ? bundle.rows : [];
	const rows = sourceRows.map((row) => {
		const mapCell = (cell) => {
			if (!cell) return cell;
			return {
				...cell,
				value: useCount ? Number(cell.orderCount) || 0 : Number(cell.amount) || 0,
				valueLabel: useCount ? cell.countLabel : cell.amountLabel,
				shareLabel: useCount ? cell.countShareLabel : cell.amountShareLabel,
				flex: useCount ? Number(cell.countFlex) || 0 : Number(cell.amountFlex) || 0,
			};
		};
		return {
			...row,
			flex: useCount ? Number(row.countFlex) || 0 : Number(row.amountFlex) || 0,
			total: useCount ? Number(row.orderCount) || 0 : Number(row.total) || 0,
			paid: mapCell(row.paid),
			pending: mapCell(row.pending),
		};
	});

	return {
		...bundle,
		metric: useCount ? 'count' : 'amount',
		displayTotalLabel: useCount ? bundle?.totalCountLabel || '0单' : bundle?.totalLabel || '¥0',
		displayPaidShareLabel: useCount ? bundle?.paidCountShareLabel || '0.00%' : bundle?.paidShareLabel || '0.00%',
		displaySharePrefix: useCount ? '已收订单' : '已收合计',
		displayEmptyHint: useCount ? '暂无已收 / 待收单量' : '暂无已收 / 待收',
		rows,
	};
};

/** 客户端兜底：按 AR title×isPaid 累加（与后端 paymentNodeShare 一致） */

/** 客户端兜底：按 AR title×isPaid 累加（与后端 paymentNodeShare 一致） */
export const buildOverviewPaymentNodeShareRows = (orders = []) => {
	const buckets = Object.fromEntries(OVERVIEW_PAYMENT_NODE_SHARE_ROWS.map((item) => [item.key, createEmptyPaymentNodeBucket()]));
	const pendingReviewBucket = createEmptyStageBucket();
	const closedBucket = createEmptyStageBucket();
	for (const order of orders ?? []) {
		if (!isOverviewPipelineOrder(order)) continue;
		for (const row of OVERVIEW_PAYMENT_NODE_SHARE_ROWS) {
			const { paid, pending } = sumOrderOpenedArByTitle(order, row.arTitle);
			buckets[row.key].paid += paid;
			buckets[row.key].pending += pending;
			if (paid > 0) buckets[row.key].paidCount += 1;
			if (pending > 0) buckets[row.key].pendingCount += 1;
		}
		const financeKey = resolveOverviewFinanceKey(order);
		if (financeKey === 'pendingReview') {
			pendingReviewBucket.count += 1;
			pendingReviewBucket.totalPrice += Number(order.totalPrice) || 0;
		} else if (financeKey === 'closed') {
			closedBucket.count += 1;
			closedBucket.totalPrice += Number(order.totalPrice) || 0;
		}
	}
	return finalizeOverviewPaymentNodeShareBundle(buckets, pendingReviewBucket, closedBucket);
};

/** 优先 overviewPipeline.paymentNodeShare；返回六色墙 bundle */

/** 优先 overviewPipeline.paymentNodeShare；返回六色墙 bundle */
export const buildOverviewPaymentNodeShareRowsFromStats = (stats) => {
	const pipeline = stats?.overviewPipeline?.paymentNodeShare;
	const pendingReviewBucket = {
		count: Number(stats?.overviewPipeline?.finance?.pendingReview?.count) || 0,
		totalPrice: Number(stats?.overviewPipeline?.finance?.pendingReview?.totalPrice) || 0,
	};
	const closedBucket = {
		count: Number(stats?.overviewPipeline?.finance?.closed?.count) || 0,
		totalPrice: Number(stats?.overviewPipeline?.finance?.closed?.totalPrice) || 0,
	};
	if (pipeline && typeof pipeline === 'object') {
		const buckets = Object.fromEntries(
			OVERVIEW_PAYMENT_NODE_SHARE_ROWS.map((item) => [
				item.key,
				{
					paid: Number(pipeline[item.key]?.paid) || 0,
					pending: Number(pipeline[item.key]?.pending) || 0,
					paidCount: Number(pipeline[item.key]?.paidCount) || 0,
					pendingCount: Number(pipeline[item.key]?.pendingCount) || 0,
				},
			]),
		);
		return finalizeOverviewPaymentNodeShareBundle(buckets, pendingReviewBucket, closedBucket);
	}
	return buildOverviewPaymentNodeShareRows(stats?.orders);
};

/** 迷你柱：取有数据末月往前最多 7 根，并齐切片金额 / 标签（贴近原稿 7 柱视觉） */
